import { useId, useRef, useState } from "react";
import type { EmployeeStatus } from "@/lib/api";
import type { QueueEmployee, ReplacementRow, VendorContact } from "@/lib/staff-api";
import { formatWaiting } from "@/lib/formatters";
import { badgeExpiry, EMPLOYEE_STATUSES, formatCalendarDate, STATUS_LABELS } from "@/lib/roster";
import { useEmployeeAction, useFulfillReplacement, useRefreshQueues } from "@/lib/staff-hooks";
import { landingStatus, ROW_ACTIONS } from "@/lib/staff-stages";
import { ActionError } from "./ActionError";
import { PRIMARY_BUTTON, SECONDARY_BUTTON } from "./buttons";
import { CompanyLink } from "./CompanyLink";
import { EmployeeLink } from "./EmployeeLink";
import { SetStageDialog } from "./SetStageDialog";
import { ClearanceResult, VerifyPanel } from "./VerifyPanel";

const ROW = "rounded-lg border border-gray-200 bg-white p-4";
const MARKER = "rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700";

function OffboardedMarker(): React.ReactElement {
  return <span className={MARKER}>No longer employed</span>;
}

/** Where the employee is among all nine stages, and where the row's button
 *  lands them. */
function StageStepper({ current }: { current: EmployeeStatus }): React.ReactElement {
  const lands = landingStatus(current);
  return (
    <ol aria-label="Stages" className="mt-3 flex flex-wrap gap-1 text-xs">
      {EMPLOYEE_STATUSES.map((status) => {
        const isCurrent = status === current;
        const isLanding = status === lands;
        const look = isCurrent
          ? "border-gray-900 bg-amber-50 font-semibold text-gray-900"
          : isLanding
            ? "border-dashed border-blue-600 text-blue-800"
            : "border-gray-200 text-gray-500";
        return (
          <li key={status} aria-current={isCurrent ? "step" : undefined} className={`rounded border px-2 py-1 ${look}`}>
            {STATUS_LABELS[status]}
            {isLanding && <span className="sr-only"> (this button moves them here)</span>}
          </li>
        );
      })}
    </ol>
  );
}

/** "Badge made", gated on the photo: with none on file the button is disabled,
 *  the row says why, and "Photo now on file" records it when it arrives. */
function BadgeMadeAction({ employee }: { employee: QueueEmployee }): React.ReactElement {
  const noteId = useId();
  const { mutation, run, reload } = useEmployeeAction(employee);
  const noPhoto = employee.photoOnFile !== true;
  return (
    <div>
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          disabled={noPhoto || mutation.isPending}
          aria-describedby={noPhoto ? noteId : undefined}
          onClick={() => run({ action: "badge_made" })}
          className={PRIMARY_BUTTON}
        >
          Badge made
        </button>
        {noPhoto && (
          <>
            <span id={noteId} className="text-sm font-medium text-amber-800">
              No photo on file
            </span>
            <button
              type="button"
              disabled={mutation.isPending}
              onClick={() => run({ action: "photo_on_file" })}
              className={SECONDARY_BUTTON}
            >
              Photo now on file
            </button>
          </>
        )}
      </div>
      <ActionError error={mutation.error} onReload={reload} />
    </div>
  );
}

/** A one-click act from ROW_ACTIONS. */
function StageAction({ employee }: { employee: QueueEmployee }): React.ReactElement | null {
  const { mutation, run, reload } = useEmployeeAction(employee);
  const act = ROW_ACTIONS[employee.status];
  if (!act) return null;
  return (
    <div>
      <button
        type="button"
        disabled={mutation.isPending}
        onClick={() => run({ action: act.action })}
        className={PRIMARY_BUTTON}
      >
        {act.label}
      </button>
      <ActionError error={mutation.error} onReload={reload} />
    </div>
  );
}

/** The row's one primary action, chosen by stage. */
export function PrimaryAction({ employee }: { employee: QueueEmployee }): React.ReactElement | null {
  switch (employee.status) {
    case "attested_pending_verification":
      return <VerifyPanel employee={employee} />;
    case "fingerprint_appointment":
      return <ClearanceResult employee={employee} />;
    case "attestation_not_found":
      return <span className={MARKER}>Waiting on vendor</span>;
    case "entered_in_systems":
      return <BadgeMadeAction employee={employee} />;
    default:
      return <StageAction employee={employee} />;
  }
}

/** The overflow menu: the audited "Set a different stage" correction. */
export function MoreActions({ employee }: { employee: QueueEmployee }): React.ReactElement {
  const [menuOpen, setMenuOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const toggle = useRef<HTMLButtonElement>(null);
  const name = `${employee.firstName} ${employee.lastName}`;
  return (
    <div className="relative">
      <button
        ref={toggle}
        type="button"
        aria-label={`More actions for ${name}`}
        aria-expanded={menuOpen}
        onClick={() => setMenuOpen((open) => !open)}
        className={SECONDARY_BUTTON}
      >
        More actions
      </button>
      {menuOpen && (
        <div className="absolute right-0 z-10 mt-1 w-56 rounded-md border border-gray-200 bg-white p-1 shadow-lg">
          <button
            type="button"
            onClick={() => {
              setMenuOpen(false);
              setDialogOpen(true);
            }}
            className="block w-full rounded px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-100"
          >
            Set a different stage
          </button>
        </div>
      )}
      {dialogOpen && (
        <SetStageDialog employee={employee} onClose={() => setDialogOpen(false)} returnFocusRef={toggle} />
      )}
    </div>
  );
}

interface QueueRowProps {
  employee: QueueEmployee;
  /** work: the row's primary act. process: the act plus the stepper. */
  variant?: "work" | "process";
}

/** One employee on the dashboard: who, for whom, where they are, and the one
 *  thing to do next. */
export function QueueRow({ employee, variant = "work" }: QueueRowProps): React.ReactElement {
  return (
    <li className={ROW}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1">
          <p>
            <EmployeeLink employee={employee} />
          </p>
          {employee.vendor !== undefined && (
            <p className="text-sm text-gray-600">
              <CompanyLink vendorId={employee.vendorId} vendor={employee.vendor} />
            </p>
          )}
          <p className="flex flex-wrap items-center gap-2 text-sm text-gray-700">
            <span>{STATUS_LABELS[employee.status]}</span>
            <span className="font-medium text-amber-800">{formatWaiting(employee.stageSince ?? employee.updatedAt)}</span>
            {employee.inactive && <OffboardedMarker />}
          </p>
        </div>
        <PrimaryAction employee={employee} />
      </div>
      {variant === "process" && <StageStepper current={employee.status} />}
    </li>
  );
}

function originalExpiry(clearanceDate: string | null): string {
  const expiry = badgeExpiry(clearanceDate ?? undefined);
  return expiry ? formatCalendarDate(expiry) : "unknown";
}

/** One replacement badge to make. A reprint never extends a badge's life, so
 *  the row shows the original expiration as unchanged. A row whose employee
 *  record is missing still shows: a human must see it. */
export function ReplacementQueueRow({
  row,
}: {
  row: ReplacementRow & { vendor?: VendorContact | null };
}): React.ReactElement {
  const fulfill = useFulfillReplacement();
  const refresh = useRefreshQueues();
  const { employee } = row;

  if (!employee) {
    return (
      <li className={ROW}>
        <p className="font-medium text-gray-900">Employee not on file</p>
        {row.vendor !== undefined && (
          <p className="mt-1 text-sm text-gray-600">
            <CompanyLink vendorId={row.order.vendorId} vendor={row.vendor} />
          </p>
        )}
        <p className="mt-1 text-sm text-gray-700">
          Replacement order {row.order.id} names employee {row.employeeId}, who is not on file. Tell IT.
        </p>
      </li>
    );
  }

  const reload = (): void => {
    fulfill.reset();
    void refresh();
  };

  return (
    <li className={ROW}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1">
          <p>
            <EmployeeLink employee={employee} />
          </p>
          {row.vendor !== undefined && (
            <p className="text-sm text-gray-600">
              <CompanyLink vendorId={row.order.vendorId} vendor={row.vendor} />
            </p>
          )}
          <p className="flex flex-wrap items-center gap-2 text-sm text-gray-700">
            <span>Replacement — original expiration {originalExpiry(row.clearanceDate)}, unchanged by this reprint</span>
            <span className="font-medium text-amber-800">{formatWaiting(row.order.createdAt)}</span>
            {row.offboarded && <OffboardedMarker />}
          </p>
        </div>
        <div>
          <button
            type="button"
            disabled={fulfill.isPending}
            onClick={() =>
              fulfill.mutate({ orderId: row.order.id, vendorId: row.order.vendorId, employeeId: row.employeeId })
            }
            className={PRIMARY_BUTTON}
          >
            Badge made
          </button>
          <ActionError error={fulfill.error} subject="order" onReload={reload} />
        </div>
      </div>
    </li>
  );
}
